import type { Map as MapLibreMap } from "maplibre-gl";

import { Log } from "@ryde/shared";

/**
 * Same buffers MapLibre 6 asks for in `_setupPainter`, so a probe that succeeds here will not
 * still throw `GPUInitializationError` on the real canvas — and a probe that fails means we must
 * not construct the map at all (Testim / RDP / grid Chrome without GPU).
 */
const MAPLIBRE_WEBGL2_PROBE_ATTRIBUTES: WebGLContextAttributes = {
  alpha: true,
  depth: true,
  stencil: true,
  premultipliedAlpha: true,
  antialias: false,
  preserveDrawingBuffer: false,
  failIfMajorPerformanceCaveat: false,
};

let cachedIsWebGL2Available: boolean | undefined;

/**
 * The MapLibre instance only when it is still usable. `getMap()` after unmount, or a map whose
 * `remove` already ran (`_removed`), throws or returns a shell whose `painter` is gone — the same
 * class of crash as `Cannot read properties of undefined (reading 'destroy')`.
 */
export const getAliveMap = (
  mapRef: { getMap: () => unknown } | null | undefined,
): MapLibreMap | undefined => {
  if (!mapRef) {
    return undefined;
  }

  try {
    const map = mapRef.getMap();
    if (!map || typeof map !== "object") {
      return undefined;
    }

    if ("_removed" in map && map._removed) {
      return undefined;
    }

    // MapLibre 6.5 fires GPUInitializationError and returns a half-built map with no painter.
    // getBounds then crashes (`reading '0'`). Treat that shell as gone.
    if ("painter" in map) {
      const painter = Reflect.get(map, "painter");
      if (painter === undefined || painter === null) {
        return undefined;
      }
    }

    return map as MapLibreMap;
  } catch (error) {
    Log.warn("MapLibre getMap failed", error);
    return undefined;
  }
};

export const isHostElementLaidOut = (element: HTMLElement | null): boolean =>
  Boolean(element && element.offsetWidth > 0 && element.offsetHeight > 0);

/**
 * Whether the map can place a pin: it has a non-zero box and a finite center/zoom.
 * Adding a pin before that is the ride-card crash (`reading '0'` / `reading 'S'`).
 */
export const isMapLibreReadyToPlacePins = (
  map: MapLibreMap | undefined,
): boolean => {
  if (!map) {
    return false;
  }

  try {
    const container = map.getContainer();
    if (!isHostElementLaidOut(container ?? null)) {
      return false;
    }

    // getCenter/getZoom throw (or return NaN) on a half-removed map; treat that as "not ready for pins".
    const center = map.getCenter();
    const zoom = map.getZoom();

    return (
      Number.isFinite(center?.lng) &&
      Number.isFinite(center?.lat) &&
      Number.isFinite(zoom)
    );
  } catch (error) {
    Log.warn("MapLibre center/zoom probe failed", error);
    return false;
  }
};

/**
 * Resize only when the canvas has a real box.
 * `resize()` at 0×0 leaves the map unable to place pins afterwards.
 */
export const resizeMapLibreIfLaidOut = (
  map: MapLibreMap | undefined,
): boolean => {
  if (!map || !isHostElementLaidOut(map.getContainer() ?? null)) {
    return false;
  }

  try {
    map.resize();
    return true;
  } catch (error) {
    Log.warn("MapLibre resize exception", error);
    return false;
  }
};

const releaseWebGL2Probe = (gl: WebGL2RenderingContext): void => {
  gl.getExtension("WEBGL_lose_context")?.loseContext();
};

const probeMapLibreWebGL2 = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2", MAPLIBRE_WEBGL2_PROBE_ATTRIBUTES);
    if (!gl) {
      return false;
    }

    releaseWebGL2Probe(gl);
    return true;
  } catch {
    return false;
  }
};

/**
 * Whether this session can create a MapLibre 6 map. Cached: a WebGL2 context is scarce (Chrome's
 * per-process limit), so we must not probe again for every map on the page.
 */
export const isMapLibreWebGL2Available = (): boolean => {
  if (cachedIsWebGL2Available !== undefined) {
    return cachedIsWebGL2Available;
  }

  cachedIsWebGL2Available = probeMapLibreWebGL2();
  return cachedIsWebGL2Available;
};

export const resetMapLibreWebGL2AvailabilityCache = (): void => {
  cachedIsWebGL2Available = undefined;
};
